/* ══════════════════════════════════════════════════════════════════
   Woche, Tag, Kalenderwoche — rechnen, nicht beschriften.

   Hier steht **kein sichtbarer Text**. „Mo" und „Mai" sind Sprache und
   stehen in `i18n/de.json` unter `kalender`; was diese Datei liefert,
   sind Zahlen und Daten. So bleibt die Rechnerei an einer Stelle und
   die Beschriftung an einer anderen.

   **Der Bezugspunkt ist der laufende Montag, nicht ein festes Datum.**
   Im Prototyp war er auf KW 19 genagelt, weil die Beispieldaten dort
   lagen. Gegen `apps/api` ist das falsch: „heute" auf dem Brett muss
   derselbe Tag sein wie „heute" auf der Uhr im Kopf der Startseite —
   sonst steht der rote Strich in einer Woche, in der niemand arbeitet,
   und jede Belegung sieht verschoben aus.
   ══════════════════════════════════════════════════════════════════ */

/** Der Montag der Woche, in der dieser Tag liegt. Sonntag ist 0. */
export function montagVon(tag: Date): Date {
  const mo = new Date(tag.getFullYear(), tag.getMonth(), tag.getDate());
  mo.setDate(mo.getDate() - ((mo.getDay() + 6) % 7));
  return mo;
}

/** Der Montag der laufenden Woche — der Nullpunkt der Zeitrechnung im Brett. */
export const montagDieserWoche = (): Date => montagVon(new Date());

/**
 * Die Spalte, die „heute" heißt.
 *
 * Fünf Spalten, Montag bis Freitag: Am Wochenende steht der Strich auf dem
 * Freitag. Ihn dann verschwinden zu lassen wäre schlimmer — das Brett hätte
 * samstags keinen Bezugspunkt mehr.
 */
export function heuteSpalte(): number {
  return Math.min(4, (new Date().getDay() + 6) % 7);
}

/** Fünf Spalten, Montag bis Freitag. Das Wochenende steht nicht im Band —
 *  der Mietkalender entscheidet über die Rechnung, nicht über die Anzeige. */
export const SPALTEN = [0, 1, 2, 3, 4] as const;

/** Die Stundenansicht deckt einen Arbeitstag ab. */
export const STUNDEN = ["06", "07", "08", "09", "10", "11", "12", "13", "14", "15", "16", "17"];

/** Die erste Stunde der Stundenansicht — aus ihr wird die Spalte gerechnet. */
export const ERSTE_STUNDE = 6;

export const zweistellig = (n: number): string => String(n).padStart(2, "0");

/** Das Datum der Spalte in der Woche mit diesem Versatz. */
export function datumFuer(spalte: number, wocheVersatz = 0): Date {
  const tag = montagDieserWoche();
  tag.setDate(tag.getDate() + wocheVersatz * 7 + spalte);
  return tag;
}

/** „04.05." — ohne Jahr, denn das Jahr steht im Kopf. */
export function kurzDatum(d: Date): string {
  return `${zweistellig(d.getDate())}.${zweistellig(d.getMonth() + 1)}.`;
}

/** `YYYY-MM-DD` in Ortszeit — `toISOString()` wäre je nach Zone der Vortag. */
export function alsTag(d: Date): string {
  return `${d.getFullYear()}-${zweistellig(d.getMonth() + 1)}-${zweistellig(d.getDate())}`;
}

/**
 * Die Kalenderwoche nach ISO 8601.
 *
 * Gerechnet, nicht gegen einen Anker gezählt: Ein fester Anker geht über den
 * Jahreswechsel hinweg falsch, und zwar unauffällig — KW 53 gibt es, KW 0
 * nicht.
 */
export function kwVon(tag: Date): number {
  const d = new Date(tag.getFullYear(), tag.getMonth(), tag.getDate());
  // Auf den Donnerstag derselben Woche: Er bestimmt nach ISO das Jahr.
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const ersterDonnerstag = new Date(d.getFullYear(), 0, 4);
  ersterDonnerstag.setDate(ersterDonnerstag.getDate() + 3 - ((ersterDonnerstag.getDay() + 6) % 7));
  return 1 + Math.round((d.getTime() - ersterDonnerstag.getTime()) / (7 * 86_400_000));
}

export function wocheNr(wocheVersatz: number): number {
  return kwVon(datumFuer(0, wocheVersatz));
}

/** Das Fenster einer Woche als zwei Tage — so, wie `GET /planung` es erwartet. */
export function wochenFenster(wocheVersatz: number): { von: string; bis: string } {
  return { von: alsTag(datumFuer(0, wocheVersatz)), bis: alsTag(datumFuer(4, wocheVersatz)) };
}

/**
 * Wie viele Wochen liegt dieser Tag von der laufenden Woche entfernt?
 *
 * Für den Sprung aus einem Vorgang ins Brett: Ohne ihn landet ein Auftrag
 * vom Oktober auf der laufenden Woche, und das Brett zeigt — korrekt — gar
 * nichts. Gerechnet wird von Montag zu Montag, damit ein Freitag und der
 * Montag danach nicht in derselben Woche landen.
 *
 * Ohne Tag: 0, also die laufende Woche. Das ist der Fall „/planung" ohne
 * alles, und der muss bleiben, was er war.
 */
export function wochenVersatzFuer(tag: string | undefined): number {
  if (!tag) return 0;
  const [jahr, monat, tagImMonat] = tag.split("-").map(Number);
  if (!jahr || !monat || !tagImMonat) return 0;
  const ziel = montagVon(new Date(jahr, monat - 1, tagImMonat));
  const jetzt = montagDieserWoche();
  // Durch 7 Tage teilen, nicht Wochen zählen: Über die Zeitumstellung hat
  // eine Woche 167 oder 169 Stunden, und ein `round` trägt beide.
  return Math.round((ziel.getTime() - jetzt.getTime()) / (7 * 24 * 3600 * 1000));
}

/** Vier Wochen zurück, vierzehn nach vorn (P-03: es waren dreizehn — die
 *  vierzehnte, über den Jahreswechsel, fehlte) — genug für ein Quartal.
 *  Blättern reicht für „nächste Woche", nicht für „Ende Juni". */
export function wochenReihe(): {
  versatz: number; kw: number; von: Date; bis: Date; monat: number; jahr: number;
}[] {
  const reihen = [];
  for (let i = -4; i <= 14; i++) {
    const mo = datumFuer(0, i);
    const fr = datumFuer(4, i);
    reihen.push({ versatz: i, kw: kwVon(mo), von: mo, bis: fr, monat: mo.getMonth(), jahr: mo.getFullYear() });
  }
  return reihen;
}

/* ── Was der Prototyp als Konstante kannte ──
   `HEUTE_SPALTE` war eine Zahl, weil „heute" dort festgenagelt war. Sie bleibt
   als Konstante stehen, damit die Masken nicht jede für sich rechnen — aber sie
   wird beim Laden aus der Uhr gefüllt und nicht aus einem Datum im Mai. */
export const HEUTE_SPALTE: number = heuteSpalte();
